import { useEffect, useState } from "react";
import { collection, query, orderBy, limit, onSnapshot, type DocumentData } from "firebase/firestore";
import { db, call } from "../firebase";
import { friendly, toast } from "../errors";
import type { Member } from "../domain";

const STATUS: Record<string, [string, string]> = {
  ok: ["Thành công", "approved"], partial: ["Chưa trọn", "pending_review"], error: ["Lỗi", "changes_requested"], running: ["Đang chạy", ""],
};
const mb = (n = 0) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
/** 2026-10-23_013000 → 23/10/2026 lúc 01:30 */
const fmtStamp = (st: string) => { const m = st.match(/^(\d{4})-(\d{2})-(\d{2})_(\d{2})(\d{2})/); return m ? `${m[3]}/${m[2]}/${m[1]} lúc ${m[4]}:${m[5]}` : st; };
const when = (t?: { toDate?: () => Date }) => (t?.toDate ? t.toDate().toLocaleString("vi-VN") : "—");

/** ADM-03: lịch sử sao lưu hằng đêm sang Backblaze B2; Quản trị chạy ngay / kiểm tra bản mới nhất. */
export default function Backups({ me }: { me: Member }) {
  const [rows, setRows] = useState<DocumentData[] | null>(null);
  const [busy, setBusy] = useState<"" | "run" | "verify">("");
  const [checks, setChecks] = useState<{ stamp: string; checks: { name: string; ok: boolean; detail: string }[] } | null>(null);
  useEffect(() => onSnapshot(query(collection(db, "backups"), orderBy("stamp", "desc"), limit(30)), (s) => setRows(s.docs.map((d) => d.data())), () => setRows([])), []);
  const admin = me.role === "admin";
  const last = rows?.find((r) => r.status !== "running");
  const ageH = last?.finishedAt?.toMillis ? (Date.now() - last.finishedAt.toMillis()) / 36e5 : null;

  const run = async () => {
    setBusy("run");
    try { const r = await call<object, { status: string; docs: number; filesCopied: number }>("runBackupNow")({}); toast(`Sao lưu ${STATUS[r.status]?.[0].toLowerCase()}: ${r.docs} bản ghi, chép ${r.filesCopied} file.`, r.status === "ok" ? "ok" : undefined); }
    catch (e) { toast(friendly(e)); }
    setBusy("");
  };
  const verify = async () => {
    setBusy("verify");
    try { setChecks(await call<object, typeof checks>("verifyBackup")({})); } catch (e) { toast(friendly(e)); }
    setBusy("");
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div className={`card ${!last ? "" : last.status === "ok" && ageH !== null && ageH < 36 ? "" : "bkWarn"}`}>
        <b>{!rows ? "Đang tải…" : !last ? "Chưa có bản sao lưu nào" : `Bản gần nhất: ${fmtStamp(last.stamp)}`}</b>
        <p className="muted" style={{ margin: "6px 0 0", fontSize: 13.5 }}>
          Hub tự sao lưu lúc 01:30 mỗi đêm sang Backblaze B2: toàn bộ dữ liệu Firestore, cùng các file ảnh, video, văn bản chưa từng sao lưu.
          Bucket bật khóa chống xóa, khóa của Hub không có quyền xóa bản sao lưu.
          {last && ageH !== null && ageH >= 36 && <><br /><b style={{ color: "#8E3420" }}>Đã hơn 36 giờ chưa có bản sao lưu mới.</b></>}
        </p>
        {admin && <div className="row" style={{ marginTop: 12 }}>
          <button className="btn primary" disabled={!!busy} onClick={run}>{busy === "run" ? "Đang sao lưu… (vài phút)" : "Sao lưu ngay"}</button>
          <button className="btn" disabled={!!busy || !last} onClick={verify}>{busy === "verify" ? "Đang kiểm tra…" : "Kiểm tra bản mới nhất"}</button>
        </div>}
      </div>

      {checks && (
        <div className="card"><b>Kiểm tra bản {checks.stamp}</b>
          <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>{checks.checks.map((c, i) => <li key={i} style={{ color: c.ok ? undefined : "#8E3420" }}>{c.ok ? "✓" : "✗"} <b>{c.name}</b>: {c.detail}</li>)}</ul>
        </div>
      )}

      <div className="card" style={{ padding: 0 }}>
        <div className="tableWrap"><table className="table stack">
          <thead><tr><th>Mốc</th><th>Kết quả</th><th>Dữ liệu</th><th>File chép mới</th><th>Dung lượng</th><th>Thời gian</th><th>Chạy bởi</th></tr></thead>
          <tbody>
            {(rows ?? []).map((r) => (
              <tr key={r.stamp}>
                <td className="cardHead"><span className="code">{r.stamp}</span></td>
                <td data-label="Kết quả"><span className={`pill ${STATUS[r.status]?.[1] ?? ""}`}>{STATUS[r.status]?.[0] ?? r.status}</span>{r.error && <div className="note">{r.error}</div>}</td>
                <td data-label="Dữ liệu">{r.docs ?? "—"} bản ghi · {r.collections ?? 0} nhóm</td>
                <td data-label="File chép mới">{r.filesCopied ?? 0}{r.filesFailed ? ` · ${r.filesFailed} lỗi` : ""}{r.filesPending ? ` · ${r.filesPending} để sau` : ""}</td>
                <td data-label="Dung lượng">{r.bytes ? mb(r.bytes) : "—"}</td>
                <td data-label="Thời gian">{when(r.finishedAt ?? r.startedAt)}{r.ms ? ` · ${Math.round(r.ms / 1000)} giây` : ""}</td>
                <td data-label="Chạy bởi">{r.trigger === "schedule" ? "Tự động" : r.by}</td>
              </tr>))}
            {rows?.length === 0 && <tr><td colSpan={7} className="muted">Chưa có lượt sao lưu nào.</td></tr>}
          </tbody>
        </table></div>
      </div>
    </div>
  );
}
